<script setup lang="ts">
import { nextTick, ref } from "vue";
import { debounce as lodashDebounce } from "lodash";

type ForcedValue = "" | number;

const props = withDefaults(
  defineProps<{
    modelValue: ForcedValue;
    forced: boolean;
    label: string;
    /** Valeur proposée à l'activation, quand rien n'a encore été saisi. */
    initialValue?: number;
    placeholder?: string;
    debounce?: number;
  }>(),
  {
    placeholder: "",
    debounce: 200,
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: ForcedValue): void;
  (e: "update:forced", value: boolean): void;
  (e: "change"): void;
}>();

const inputRef = ref<HTMLInputElement | null>(null);

function blur() {
  inputRef.value?.blur();
}

const debouncedChange = lodashDebounce(() => emit("change"), props.debounce);

// Seule la case relâche le forçage. Vider le champ en cours de saisie (effacer
// « 12 » pour taper « 8 ») ne doit PAS décocher la case : le champ serait
// démonté sous le doigt, le focus perdu, et recocher réinjecterait la hauteur
// du style. Un champ vide mais coché n'applique simplement rien
// (cf. ScaleMenuState.hasForcedSize).
function handleValueInput(e: Event) {
  const raw = (e.target as HTMLInputElement).value;
  emit("update:modelValue", raw === "" ? "" : (raw as unknown as ForcedValue));
  debouncedChange();
}

function handleCheckboxChange(e: Event) {
  const checked = (e.target as HTMLInputElement).checked;
  emit("update:forced", checked);
  // Partir de la valeur en vigueur plutôt que d'un champ vide : forcer, c'est
  // presque toujours ajuster ce qu'on a déjà.
  if (checked && props.modelValue === "" && props.initialValue !== undefined) {
    emit("update:modelValue", props.initialValue);
  }
  // Le champ n'existe que si la case est cochée : on y amène l'utilisateur,
  // sinon cocher ne semble rien faire tant qu'aucune valeur n'est saisie.
  if (checked) nextTick(() => inputRef.value?.focus());
  debouncedChange();
}
</script>

<template>
  <!-- Réglage qui prend le pas sur un autre : une fois actif, il se signale
       (fond et liseré orangés) plutôt que de rester une case cochée discrète.
       @click.stop : dans un menu déroulant Bootstrap, le premier clic ailleurs
       que sur un champ referme le menu — ici on vient y régler une valeur. -->
  <div class="force-input" :class="{ 'is-forced': forced }" @click.stop>
    <label class="force-row">
      <input
        class="form-check-input m-0"
        type="checkbox"
        :checked="forced"
        @change="handleCheckboxChange"
      />
      <span class="force-label">{{ label }}</span>
      <form v-if="forced" class="force-form" @submit.prevent="blur()">
        <!-- enterkeyhint + blur explicite sur Entrée : certains claviers Android
             (Huawei) ne se replient pas sur la seule soumission du formulaire. -->
        <input
          ref="inputRef"
          type="number"
          inputmode="decimal"
          enterkeyhint="done"
          class="form-control force-value"
          step="any"
          :placeholder="placeholder"
          :value="modelValue"
          @input="handleValueInput"
          @keydown.enter.prevent="blur()"
        />
      </form>
    </label>
  </div>
</template>

<style lang="scss" scoped>
.force-input {
  padding: 8px 10px;
  border: 1px solid transparent;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.04);
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;
}

.force-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  cursor: pointer;
}

.force-label {
  flex: 1;
  min-width: 0;
  color: #fff;
  font-size: 12px;
  white-space: nowrap;
}

.force-form {
  flex-shrink: 0;
}

.force-value {
  width: 58px;
  height: 28px;
  padding: 0 8px;
  font-size: 12px;
  text-align: right;
}

.is-forced {
  border-color: color-mix(in srgb, var(--canvas-forced) 45%, transparent);
  background-color: color-mix(in srgb, var(--canvas-forced) 12%, transparent);

  .force-label {
    color: var(--canvas-forced);
  }

  .form-check-input:checked {
    background-color: var(--canvas-forced);
    border-color: var(--canvas-forced);
  }
}
</style>
